import React, { useState, useEffect } from "react";
import moment from "moment";
import { apiUrls } from "../utils/apiUrls";
import { callAPI } from "../utils/apiUtils";
import * as XLSX from "xlsx";
import MainLayoutWithoutFooter from "../components/MainLayoutWithoutFooter";

import RevenueEditModal from "../components/modals/RevenueEditModal";
import RevenueCalculatorModal from "../components/modals/RevenueCalculatorModal";
import ValidatePinModal from "../components/modals/ValidatePinModal";
import RevenueAutomationSettingsModal from "../components/modals/RevenueAutomationSettingsModal";
import DeletedDetailModal from "../components/modals/DeletedDetailModal";
import RevenueReportMenu from "../components/RevenueReportMenu";
import { toast } from 'react-toastify';

const Revenue = () => {
  const [selectedDate, setSelectedDate] = useState(moment().format("DD/MM/YYYY"));
  const [revenues, setRevenues] = useState([]);
  const [revenueId, setRevenueId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailModal, setDetailModal] = useState(null); // { type, employeeId, employeeName }

  const [modalType, setModalType] = useState(null); // 'edit' or 'calc'
  const [modalData, setModalData] = useState(null);
  const [sendingPdf, setSendingPdf] = useState(false);
  const [pinValidated, setPinValidated] = useState(false);
  const [showPinModal, setShowPinModal] = useState(true);
  const [showAutomationSettings, setShowAutomationSettings] = useState(false);

  const [rangeMode, setRangeMode] = useState(false);
  const [rangeStart, setRangeStart] = useState(moment().format("YYYY-MM-DD"));
  const [rangeEnd, setRangeEnd] = useState(moment().format("YYYY-MM-DD"));
  const [reservationsBreakdown, setReservationsBreakdown] = useState(null);
  const [deletedReport, setDeletedReport] = useState({ rows: [] });
  const [salesByCategory, setSalesByCategory] = useState({ rows: [], total_revenue: 0, total_qty_sold: 0 });
  const [salesBySubCategory, setSalesBySubCategory] = useState({ rows: [], total_revenue: 0, total_qty_sold: 0 });
  const [loadingExtras, setLoadingExtras] = useState(false);

  useEffect(() => {
    if (pinValidated) {
        fetchRevenues();
    }
}, [pinValidated, selectedDate]);

  useEffect(() => {
    if (pinValidated) {
      fetchReservationsAndDeletions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinValidated, selectedDate, rangeMode, rangeStart, rangeEnd]);

  const getReportDateRange = () => {
    if (rangeMode) {
      return {
        start: moment(rangeStart, "YYYY-MM-DD").format("DD/MM/YYYY"),
        end: moment(rangeEnd, "YYYY-MM-DD").format("DD/MM/YYYY"),
      };
    }
    return { start: selectedDate, end: selectedDate };
  };

  const fetchReservationsAndDeletions = async () => {
    const { start, end } = getReportDateRange();
    setLoadingExtras(true);
    try {
      const [resvRes, delRes, catRes, subCatRes] = await Promise.all([
        callAPI(`${apiUrls.REVENUE_RESERVATIONS_BREAKDOWN}?start_date=${start}&end_date=${end}`),
        callAPI(`${apiUrls.REVENUE_DELETED_ITEMS_REPORT}?start_date=${start}&end_date=${end}`),
        callAPI(`${apiUrls.REVENUE_SALES_BY_CATEGORY}?start_date=${start}&end_date=${end}`),
        callAPI(`${apiUrls.REVENUE_SALES_BY_SUB_CATEGORY}?start_date=${start}&end_date=${end}`),
      ]);
      setReservationsBreakdown(resvRes.data?.data || null);
      setDeletedReport(delRes.data?.data || { rows: [] });
      setSalesByCategory(catRes.data?.data || { rows: [], total_revenue: 0, total_qty_sold: 0 });
      setSalesBySubCategory(subCatRes.data?.data || { rows: [], total_revenue: 0, total_qty_sold: 0 });
    } catch (err) {
      console.error("Failed to fetch reservations/deletions report:", err);
    } finally {
      setLoadingExtras(false);
    }
  };

  const mergedDeletionRows = deletedReport.rows || [];

  const fetchRevenues = async () => {
    setLoading(true);
    try {
      const res = await callAPI(`${apiUrls.REVENUE_LISTING}?date=${selectedDate}`);
      if (res.data.data) {
        setRevenues(res.data.data.revenues);
        setRevenueId(res.data.data.id);
      }
    } catch (err) {
      console.error("Failed to fetch revenue data:", err);
    } finally {
      setLoading(false);
    }
  };

  const validateRevenuePin = async (pin) => {
    const res = await callAPI(
        apiUrls.REVENUE_VALIDATE_PIN,
        {},
        "POST",
        { pin }
    );

    if (res.data.status) {
        setPinValidated(true);
        setShowPinModal(false);
    } else {
        toast.error(res.data.message);
    }
};

  const openModal = (header, column, value) => {
    const isCash =
      column === "pos_cash_revenue_cashier_reported_revenue" ||
      column === "pos_cash_revenue_cashier_reported_refunds";

    setModalType(isCash ? "calc" : "edit");
    setModalData({ header, column, value });
  };

  const closeModal = () => {
    setModalData(null);
    setModalType(null);
  };



  const handleSave = async (newValue) => {

    if (!modalData) {
      console.log("Missing modal data");
      return;
    }

    console.log("SAVE CLICKED", {
      modalData,
      revenueId,
      newValue
    });

    const payload = {
      id: revenueId || "",
      column: modalData.column,
      value: newValue,
      date: selectedDate,
    };
  
    try {
      setLoading(true);
       const res = await callAPI(apiUrls.REVENUE_UPDATE, {}, 'POST', payload, {});
      
  
      if (res.data?.status === 1) {
       
        await fetchRevenues();
      } else {
        console.error("Revenue update failed:", res.data);
        alert("Failed to update revenue.");
      }
    } catch (err) {
      console.error("Error updating revenue:", err);
      alert("An error occurred while saving revenue data.");
    } finally {
      setLoading(false);
      closeModal();
    }
  };
  
  
  const changeDate = (direction) => {
    const newDate =
      direction === "prev"
        ? moment(selectedDate, "DD/MM/YYYY").subtract(1, "days")
        : moment(selectedDate, "DD/MM/YYYY").add(1, "days");
    setSelectedDate(newDate.format("DD/MM/YYYY"));
  };

  const handleCloseTill = async () => {
    if (!window.confirm("Are you sure you'd like to close till for the day?")) return;

    try {
      setSendingPdf(true);

    
      const res = await callAPI(`${apiUrls.REVENUE_PDF_DATA_FORMAT}?date=${selectedDate}`);
      const data = res.data?.data;
      if (!data) throw new Error("No data received for PDF generation");

     
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.aoa_to_sheet(data);
      XLSX.utils.book_append_sheet(workbook, worksheet, "Revenue");

      const xlsBlob = new Blob(
        [new Uint8Array(XLSX.write(workbook, { bookType: "xlsx", type: "array" }))],
        { type: "application/octet-stream" }
      );

     
      const filename = `${Math.floor(Math.random() * 1000000000)}_${moment().unix()}.xlsx`;
      const formData = new FormData();
      formData.append("xls", xlsBlob, filename);
      formData.append("selected_date", selectedDate);

    
      const uploadRes = await callAPI(apiUrls.REVENUE_SEND_PDF, {}, "POST", formData, {},
         { "Content-Type": "multipart/form-data" },
      );

      if (uploadRes.data?.status === 1) {
        alert("Mail sent successfully!");
      } else {
        alert("Failed to send till report.");
      }
    } catch (err) {
      console.error("Error sending till report:", err);
      alert("An error occurred while closing till.");
    } finally {
      setSendingPdf(false);
    }
  };

  return (
    <MainLayoutWithoutFooter>
      <div className="pageContent revenuePage pb-4">
        <div className="container">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3 mb-lg-4">
            <h2 className="lgTitle mb-0">Revenue</h2>
            <div className="d-flex justify-content-end align-items-center gap-2 flex-wrap">
              <button
                className="btn btnPrimary"
                disabled={sendingPdf}
                onClick={handleCloseTill}
              >
                {sendingPdf ? (
                  <>
                    <i className="fa fa-spinner fa-spin me-2"></i> Closing Till...
                  </>
                ) : (
                  <span>Close Till</span>
                )}
              </button>
              <RevenueReportMenu date={selectedDate} />
              <button
                type="button"
                className="btn iconBtn"
                style={{ background: "#2a2a2a", color: "#fff" }}
                onClick={() => setShowAutomationSettings(true)}
              >
                Automation Settings
              </button>
            </div>
          </div>


          <div className="table-responsive revenueTableCol mb-4">
            <table className="table table-striped table-bordered text-nowrap">
              <thead>
                <tr>
                  <th className="stickyCol">
                    <div className="d-flex justify-content-between align-items-center gap-3">
                      <button className="simpleIconBtn" onClick={() => changeDate("prev")}>
                        <svg width="24" height="24" className="circleArrowIcon">
                          <use xlinkHref="./images/sprite.svg#iconPrevArrow2"></use>
                        </svg>
                      </button>
                      <span>{selectedDate}</span>
                      <button className="simpleIconBtn" onClick={() => changeDate("next")}>
                        <svg width="24" height="24" className="circleArrowIcon">
                          <use xlinkHref="./images/sprite.svg#iconNextArrow2"></use>
                        </svg>
                      </button>
                    </div>
                  </th>
                  <th>System Reported</th>
                  <th>Cashier Reported</th>
                  <th>Cashier Reported Refunds</th>
                  <th>Refunds</th>
                  <th>Difference</th>
                  <th>Previous Till Balance</th>
                  <th>Current Till Balance</th>
                  <th>Net Revenue</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="9" className="text-center">
                      Loading...
                    </td>
                  </tr>
                ) : (
                  Object.values(revenues).map((rev, idx) => (
                    <tr key={idx}>
                      
                      <td className="stickyCol text-center">
                        {rev.title}
                      </td>
                      
                      <td className="text-center">
                        {rev.system_reported_revenue}
                      </td>
                     
                      <td>
                        <div className="d-flex justify-content-between align-items-center">
                          <span>{rev.cashier_reported_revenue}</span>

                          {rev.cashier_reported_revenue_add && (
                            <button
                              className="simpleIconBtn"
                              onClick={() =>
                                openModal(
                                  rev.title,
                                  rev.cashier_reported_revenue_column,
                                  rev.cashier_reported_revenue
                                )
                              }
                            >
                              <svg width="14" height="14">
                                <use xlinkHref="./images/sprite.svg#iconPlus"></use>
                              </svg>
                            </button>
                          )}

                        </div>
                      </td>


                      
                      <td>
                        <div className="d-flex justify-content-between align-items-center">

                          <span>{rev.cashier_reported_refunds}</span>

                          {rev.cashier_reported_refunds_add && (
                            <button
                              className="simpleIconBtn"
                              onClick={() =>
                                openModal(
                                  rev.title,
                                  rev.cashier_reported_refunded_column,
                                  rev.cashier_reported_refunds
                                )
                              }
                            >
                              <svg width="14" height="14">
                                <use xlinkHref="./images/sprite.svg#iconPlus"></use>
                              </svg>
                            </button>
                          )}

                        </div>
                      </td>


                      
                      <td className="text-center">
                        {rev.order_refund_total}
                      </td>


                      
                      <td>
                        <div className="d-flex justify-content-between align-items-center">

                          <span>
                            {rev.difference}
                          </span>


                          {rev.difference_comment && (
                            <button
                              className="simpleIconBtn"
                              onClick={() =>
                                openModal(
                                  rev.title,
                                  rev.difference_column,
                                  rev.difference_comment_value
                                )
                              }
                            >
                              <svg width="14" height="14">
                                <use xlinkHref="./images/sprite.svg#iconComment"></use>
                              </svg>
                            </button>
                          )}

                        </div>
                      </td>


                      
                      <td>
                        {rev.previous_till_balance}
                      </td>

                      <td>
                        <div className="d-flex justify-content-between align-items-center">

                          <span>
                            {rev.current_till_balance}
                          </span>

                          {rev.current_till_balance_add && (
                            <button
                              className="simpleIconBtn"
                              onClick={() =>
                                openModal(
                                  rev.title,
                                  rev.current_till_balance_column,
                                  rev.current_till_balance
                                )
                              }
                            >
                              <svg width="14" height="14">
                                <use xlinkHref="./images/sprite.svg#iconPlus"></use>
                              </svg>
                            </button>
                          )}

                        </div>
                      </td>

                      <td className="text-center">
                        {rev.net_revenue}
                      </td>


                    </tr>
                  ))
                )}
                </tbody>
            </table>
          </div>
          {showAutomationSettings && (
            <RevenueAutomationSettingsModal
              isOpen={true}
              onClose={() => setShowAutomationSettings(false)}
            />
          )}

          <div className="d-flex justify-content-end align-items-center flex-wrap gap-2 mb-3 mt-5">
            <label className="d-flex align-items-center gap-2 mb-0" style={{ fontSize: 14, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={rangeMode}
                onChange={(e) => setRangeMode(e.target.checked)}
              />
              Custom range
            </label>
            {rangeMode && (
              <>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  style={{ width: 150 }}
                  value={rangeStart}
                  onChange={(e) => setRangeStart(e.target.value)}
                />
                <span>to</span>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  style={{ width: 150 }}
                  value={rangeEnd}
                  onChange={(e) => setRangeEnd(e.target.value)}
                />
              </>
            )}
          </div>

          <h2 className="lgTitle mb-3">Reservations</h2>
          <div className="table-responsive revenueTableCol mb-4">
            <table className="table table-striped table-bordered text-nowrap">
              <thead>
                <tr>
                  <th className="text-center">Pending</th>
                  <th className="text-center">Accepted</th>
                  <th className="text-center">Rejected</th>
                  <th className="text-center">Cancelled</th>
                  <th className="text-center">No-show</th>
                  <th className="text-center">Total</th>
                </tr>
              </thead>
              <tbody>
                {loadingExtras ? (
                  <tr>
                    <td colSpan="6" className="text-center">Loading...</td>
                  </tr>
                ) : (
                  <tr>
                    <td className="text-center">{reservationsBreakdown?.pending ?? 0}</td>
                    <td className="text-center">{reservationsBreakdown?.accepted ?? 0}</td>
                    <td className="text-center">{reservationsBreakdown?.rejected ?? 0}</td>
                    <td className="text-center">{reservationsBreakdown?.cancelled ?? 0}</td>
                    <td className="text-center">{reservationsBreakdown?.no_show ?? 0}</td>
                    <td className="text-center"><strong>{reservationsBreakdown?.total ?? 0}</strong></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <h2 className="lgTitle mb-3">Deleted Items &amp; Orders by Staff</h2>
          <div className="table-responsive revenueTableCol mb-4">
            <table className="table table-striped table-bordered text-nowrap">
              <thead>
                <tr>
                  <th>Staff Member</th>
                  <th className="text-center">Items Deleted</th>
                  <th className="text-center">Orders Deleted</th>
                </tr>
              </thead>
              <tbody>
                {loadingExtras ? (
                  <tr>
                    <td colSpan="3" className="text-center">Loading...</td>
                  </tr>
                ) : mergedDeletionRows.length === 0 ? (
                  <tr>
                    <td colSpan="3" className="text-center">No deletions recorded</td>
                  </tr>
                ) : (
                  mergedDeletionRows.map((row) => {
                    const employeeName = (row.employee_name || "").trim() || "Unknown staff";
                    return (
                      <tr key={row.employee_id ?? "unknown"}>
                        <td>{employeeName}</td>
                        <td className="text-center">
                          {row.items_deleted_count ? (
                            <button
                              type="button"
                              className="underlineLink"
                              onClick={() =>
                                setDetailModal({ type: "items", employeeId: row.employee_id ?? null, employeeName })
                              }
                            >
                              {row.items_deleted_count}
                            </button>
                          ) : (
                            0
                          )}
                        </td>
                        <td className="text-center">
                          {row.orders_deleted_count ? (
                            <button
                              type="button"
                              className="underlineLink"
                              onClick={() =>
                                setDetailModal({ type: "orders", employeeId: row.employee_id ?? null, employeeName })
                              }
                            >
                              {row.orders_deleted_count}
                            </button>
                          ) : (
                            0
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <h2 className="lgTitle mb-3">Sales by Categories</h2>
          <div className="table-responsive revenueTableCol mb-4">
            <table className="table table-striped table-bordered text-nowrap">
              <thead>
                <tr>
                  <th>Category</th>
                  <th className="text-center">Qty Sold</th>
                  <th className="text-center">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {loadingExtras ? (
                  <tr>
                    <td colSpan="3" className="text-center">Loading...</td>
                  </tr>
                ) : salesByCategory.rows.length === 0 ? (
                  <tr>
                    <td colSpan="3" className="text-center">No sales recorded</td>
                  </tr>
                ) : (
                  <>
                    {salesByCategory.rows.map((row) => (
                      <tr key={row.id ?? "unknown"}>
                        <td>{row.name}</td>
                        <td className="text-center">{row.qty_sold}</td>
                        <td className="text-center">${row.revenue}</td>
                      </tr>
                    ))}
                    <tr>
                      <td><strong>Total</strong></td>
                      <td className="text-center"><strong>{salesByCategory.total_qty_sold}</strong></td>
                      <td className="text-center"><strong>${salesByCategory.total_revenue}</strong></td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>

          <h2 className="lgTitle mb-3">Sales by Sub Categories</h2>
          <div className="table-responsive revenueTableCol mb-4">
            <table className="table table-striped table-bordered text-nowrap">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Sub Category</th>
                  <th className="text-center">Qty Sold</th>
                  <th className="text-center">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {loadingExtras ? (
                  <tr>
                    <td colSpan="4" className="text-center">Loading...</td>
                  </tr>
                ) : salesBySubCategory.rows.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="text-center">No sales recorded</td>
                  </tr>
                ) : (
                  <>
                    {salesBySubCategory.rows.map((row) => (
                      <tr key={`${row.category_id ?? "unknown"}-${row.id ?? "none"}`} className={row.has_breakdown ? "" : "text-muted"}>
                        <td>{row.category_name}</td>
                        <td>{row.has_breakdown ? row.name : <em>No sub-category breakdown — see Sales by Categories</em>}</td>
                        <td className="text-center">{row.qty_sold}</td>
                        <td className="text-center">${row.revenue}</td>
                      </tr>
                    ))}
                    <tr>
                      <td colSpan="2"><strong>Total</strong></td>
                      <td className="text-center"><strong>{salesBySubCategory.total_qty_sold}</strong></td>
                      <td className="text-center"><strong>${salesBySubCategory.total_revenue}</strong></td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>

          {modalType === "edit" && (
            <RevenueEditModal
              isOpen={true}
              title={modalData?.header}
              defaultValue={modalData?.value}
              onClose={closeModal}
              onSave={handleSave}
            />
          )}

          {modalType === "calc" && (
            <RevenueCalculatorModal
              isOpen={true}
              title={modalData?.header}
              defaultValue={modalData?.value}
              onClose={closeModal}
              onSave={handleSave}
            />
          )}
        </div>
      </div>

      {detailModal && (
        <DeletedDetailModal
          isOpen={true}
          onClose={() => setDetailModal(null)}
          type={detailModal.type}
          employeeId={detailModal.employeeId}
          employeeName={detailModal.employeeName}
          startDate={getReportDateRange().start}
          endDate={getReportDateRange().end}
        />
      )}

      <ValidatePinModal
    isOpen={showPinModal}
    onCancel={() => {}}
    onConfirm={validateRevenuePin}
    allowClose={false}
/>
    </MainLayoutWithoutFooter>
  );
};

export default Revenue;
